// youtubeIframeApi — carregamento único do script global da YouTube IFrame
// Player API + tipos compartilhados de window.YT.
//
// Extraído de YoutubeSlide.tsx (player da playlist) para ser reutilizado por
// MomentoEspecialYoutubeAudio.tsx (player dedicado do momento especial de
// aniversário). Motivo técnico, não só de estilo: duas declarações `declare
// global { interface Window { YT?: ... } }` em arquivos diferentes, cada uma
// com sua própria interface local para o tipo do construtor `Player`, fazem
// o TypeScript recusar o merge das declarações globais (TS2717 — "Subsequent
// property declarations must have the same type") mesmo quando as interfaces
// são estruturalmente idênticas. Centralizar aqui resolve o conflito na raiz
// e evita duplicar a lógica de carregamento do script `iframe_api`.
export interface YTPlayerEvent { data: number; }

export interface YTPlayerOptions {
  videoId: string;
  width?:  number | string;
  height?: number | string;
  playerVars?: Record<string, string | number>;
  events?: {
    onReady?:                 (e: { target: YTPlayerInstance }) => void;
    onStateChange?:           (e: YTPlayerEvent) => void;
    onError?:                 (e: YTPlayerEvent) => void;
    onPlaybackQualityChange?: (e: { target: YTPlayerInstance; data: string }) => void;
  };
}

export interface YTPlayerInstance {
  destroy():    void;
  playVideo():  void;
  pauseVideo(): void;
  mute():       void;
  unMute():     void;
  setVolume(volume: number): void;
  loadVideoById(videoId: string, startSeconds?: number): void;
  getCurrentTime?():      number;
  getPlaybackQuality?(): string;
  unloadModule?(module: string): void;
  setOption?(module: string, option: string, value: unknown): void;
}

declare global {
  interface Window {
    YT?: {
      Player: new (el: HTMLElement, opts: YTPlayerOptions) => YTPlayerInstance;
    };
    onYouTubeIframeAPIReady?: () => void;
    __ytReadyCallbacks?: Array<() => void>;
  }
}

/** Injeta (uma única vez) o script `iframe_api` e resolve quando `window.YT.Player` fica disponível. */
export function carregarYouTubeAPI(): Promise<void> {
  return new Promise(resolve => {
    if (window.YT?.Player) { resolve(); return; }
    window.__ytReadyCallbacks ??= [];
    window.__ytReadyCallbacks.push(resolve);
    if (document.querySelector('script[src*="youtube.com/iframe_api"]')) return;
    window.onYouTubeIframeAPIReady = () => {
      (window.__ytReadyCallbacks ?? []).forEach(cb => cb());
      window.__ytReadyCallbacks = [];
    };
    injetarScriptIframeApi();
  });
}

const RETRY_SCRIPT_MS = 10_000;

// Id do retry pendente: sem guardá-lo, um sucesso (ou uma nova chamada de carregarYouTubeAPI) no meio
// do intervalo não o cancelava e o retry acrescentava uma 2ª tag iframe_api por cima de um player vivo.
let retryTimer: ReturnType<typeof setTimeout> | null = null;

function cancelarRetry(): void {
  if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
}

// Sem onerror, uma falha de rede no boot da TV deixava a tag no DOM e toda
// chamada seguinte só empilhava callbacks que nunca resolviam (YoutubeSlide
// preto até reload manual). Remove a tag com falha e tenta de novo; os
// callbacks pendentes continuam esperando.
function injetarScriptIframeApi(): void {
  cancelarRetry();
  if (window.YT?.Player || document.querySelector('script[src*="youtube.com/iframe_api"]')) return;

  const script  = document.createElement('script');
  script.src    = 'https://www.youtube.com/iframe_api';
  script.async  = true;
  script.onerror = () => {
    script.remove();
    cancelarRetry();
    retryTimer = setTimeout(injetarScriptIframeApi, RETRY_SCRIPT_MS);
  };
  document.head.appendChild(script);
}
